import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import { CategoryIcon, ICON_OPTIONS, DEFAULT_ICON } from '../components/CategoryIcons';

export default function ServiceCategoriesPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // modal: null = closed | 'add' | category object = edit
  const [modal, setModal] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formIcon, setFormIcon] = useState(DEFAULT_ICON);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    setFetchError('');
    try {
      const res = await apiFetch('/api/admin/service-categories', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setFetchError("Kategoriyalarni yuklashda xatolik yuz berdi. Sahifani yangilang.");
    } finally {
      setPageLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const openAdd = () => {
    setFormName('');
    setFormDesc('');
    setFormIcon(DEFAULT_ICON);
    setFormError('');
    setModal('add');
  };

  const openEdit = (cat) => {
    setFormName(cat.name);
    setFormDesc(cat.description || '');
    setFormIcon(cat.iconName || DEFAULT_ICON);
    setFormError('');
    setModal(cat);
  };

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    setFormError('');
    if (!formName.trim()) {
      setFormError("Nom maydoni majburiy");
      return;
    }
    setSaving(true);
    try {
      const isEdit = modal !== 'add';
      const path = isEdit
        ? `/api/admin/service-categories/${modal.id}`
        : '/api/admin/service-categories';
      const res = await apiFetch(path, auth.token, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify({
          name: formName.trim(),
          description: formDesc.trim() || null,
          iconName: formIcon || DEFAULT_ICON,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFormError(d.message || 'Saqlashda xatolik yuz berdi');
        return;
      }
      closeModal();
      await loadCategories();
    } catch {
      setFormError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/service-categories/${deleteTarget.id}`, auth.token, {
        method: 'DELETE',
      });
      setDeleteTarget(null);
      await loadCategories();
    } catch {} finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>Xizmat kategoriyalari</h1>
          <p style={s.subtitle}>Barcha xizmat turlarini boshqaring</p>
        </div>
        <button style={s.addBtn} onClick={openAdd}>+ Qo'shish</button>
      </div>

      {fetchError && <div style={s.errorBanner}>{fetchError}</div>}

      {pageLoading ? (
        <div style={s.centerMsg}>Yuklanmoqda...</div>
      ) : categories.length === 0 && !fetchError ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📂</div>
          <p style={s.emptyText}>Hali kategoriya yo'q</p>
          <p style={s.emptyHint}>Birinchi kategoriyani qo'shish uchun "+ Qo'shish" tugmasini bosing.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '48px' }}>#</th>
                <th style={{ ...s.th, width: '64px' }}>Ikon</th>
                <th style={s.th}>Nomi</th>
                <th style={s.th}>Tavsif</th>
                <th style={{ ...s.th, width: '220px', textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, i) => (
                <tr key={cat.id} style={s.tr}>
                  <td style={{ ...s.td, color: 'var(--text-faint)' }}>{i + 1}</td>
                  <td style={s.td}>
                    <span style={s.iconBadge}>
                      <CategoryIcon name={cat.iconName || DEFAULT_ICON} size={22} color="#e94560" />
                    </span>
                  </td>
                  <td style={{ ...s.td, fontWeight: '600', color: 'var(--text)' }}>{cat.name}</td>
                  <td style={{ ...s.td, color: 'var(--text-muted)' }}>
                    {cat.description || <span style={{ fontStyle: 'italic', color: 'var(--border-strong)' }}>—</span>}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <div style={s.actions}>
                      <button
                        style={s.propBtn}
                        onClick={() => navigate(`/admin/service-categories/${cat.id}/fields`)}
                      >
                        Xususiyatlar
                      </button>
                      <button style={s.editBtn} onClick={() => openEdit(cat)}>Tahrir</button>
                      <button style={s.delBtn} onClick={() => setDeleteTarget(cat)}>O'chirish</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {modal !== null && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>
              {modal === 'add' ? 'Yangi kategoriya' : 'Kategoriyani tahrirlash'}
            </h2>

            <div style={s.field}>
              <label style={s.label}>Nomi <span style={s.req}>*</span></label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                style={s.input}
                placeholder="Kategoriya nomini kiriting"
                autoFocus
                disabled={saving}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Tavsif</label>
              <textarea
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                style={s.textarea}
                placeholder="Ixtiyoriy tavsif"
                rows={3}
                disabled={saving}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Ikon</label>
              <div style={s.iconGrid}>
                {ICON_OPTIONS.map((opt) => {
                  const selected = formIcon === opt.name;
                  return (
                    <button
                      type="button"
                      key={opt.name}
                      title={opt.label}
                      onClick={() => !saving && setFormIcon(opt.name)}
                      style={{ ...s.iconCell, ...(selected ? s.iconCellSelected : {}) }}
                      disabled={saving}
                    >
                      <CategoryIcon
                        name={opt.name}
                        size={24}
                        color={selected ? '#e94560' : '#4b5563'}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {formError && <div style={s.formError}>{formError}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeModal} disabled={saving}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...s.modalCard, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>O'chirishni tasdiqlang</h2>
            <p style={s.confirmText}>
              <strong>"{deleteTarget.name}"</strong> kategoriyasini o'chirishni xohlaysizmi?
              Bu amalni qaytarib bo'lmaydi.
            </p>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.deleteConfirmBtn, ...(deleting ? s.saveBtnDisabled : {}) }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "O'chirilmoqda..." : "O'chirish"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const s = {
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: {
    margin: '0 0 4px',
    fontSize: '22px',
    fontWeight: '700',
    color: 'var(--text)',
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  addBtn: {
    padding: '10px 20px',
    background: 'var(--brand)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  errorBanner: {
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '12px 16px',
    color: 'var(--danger)',
    fontSize: '14px',
    marginBottom: '16px',
  },
  centerMsg: {
    textAlign: 'center',
    padding: '60px',
    color: 'var(--text-faint)',
    fontSize: '15px',
  },
  empty: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'var(--surface)',
    borderRadius: '12px',
    border: '1px dashed #d1d5db',
  },
  emptyIcon: {
    fontSize: '40px',
    marginBottom: '12px',
  },
  emptyText: {
    margin: '0 0 6px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
  },
  emptyHint: {
    margin: 0,
    fontSize: '14px',
    color: 'var(--text-faint)',
  },
  tableWrap: {
    background: 'var(--surface)',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: 'var(--text-muted)',
    background: 'var(--bg)',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '14px 16px',
    verticalAlign: 'middle',
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  propBtn: {
    padding: '5px 12px',
    background: 'var(--info-tint)',
    color: 'var(--info)',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  editBtn: {
    padding: '5px 12px',
    background: 'var(--success-tint)',
    color: 'var(--success)',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  delBtn: {
    padding: '5px 12px',
    background: 'var(--danger-tint)',
    color: 'var(--danger)',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '16px',
  },
  modalCard: {
    background: 'var(--surface)',
    borderRadius: '14px',
    padding: '28px 28px 24px',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    margin: '0 0 20px',
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text)',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
  },
  req: {
    color: 'var(--brand)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'var(--surface)',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
    background: 'var(--surface)',
  },
  formError: {
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '10px 12px',
    color: 'var(--danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '8px',
  },
  cancelBtn: {
    padding: '9px 18px',
    background: 'var(--surface-alt)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  saveBtn: {
    padding: '9px 22px',
    background: 'var(--brand)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  saveBtnDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
  deleteConfirmBtn: {
    padding: '9px 22px',
    background: 'var(--danger)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  confirmText: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: '1.6',
    margin: '0 0 20px',
  },
  iconBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: '#fdf2f4',
    border: '1px solid #f9d7de',
  },
  iconGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
    gap: '8px',
  },
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    background: 'var(--surface)',
    border: '1.5px solid #e5e7eb',
    borderRadius: '10px',
    cursor: 'pointer',
    padding: 0,
    transition: 'border-color 0.15s, background 0.15s',
  },
  iconCellSelected: {
    borderColor: 'var(--brand)',
    background: '#fdf2f4',
    boxShadow: '0 0 0 2px rgba(233,69,96,0.15)',
  },
};
